import {
  allocateSaleTenders,
  buildSaleQuoteRequest,
  collectSaleCompliance,
  INVOICE_REPRINT_AUDIT_REASON,
  requiredSaleComplianceFields,
  type AcceptSaleResponse,
  type SaleQuoteResponse,
} from "@medicorepay/shared";
import { useEffect, useState } from "react";
import { PrescriptionUpload } from "../../components/prescription/PrescriptionUpload";
import { openInvoicePrintWindow } from "../../lib/revival-invoice-print";
import { showToast } from "../../lib/toast";
import { productRevivalApi } from "../../services/product-revival-client";
import { paymentService } from "../../services/payment.service";
import { printerDispatchService } from "../../services/printer-dispatch.service";
import { printerService } from "../../services/printer.service";
import { useAuthStore } from "../../stores/authStore";
import {
  useBillingSessionStore,
  useCanBill,
} from "../../stores/billingSessionStore";

import {
  CartTable,
  CustomerPanel,
  POSSessionTabs,
  POSSidebar,
  PaymentModal,
  ProductSearchBar,
  SaleSuccessModal,
} from "./components";
import type { SplitPaymentLine } from "./components/PaymentModal";
import { useDiscountEngine } from "./hooks/useDiscountEngine";
import { usePOSCart } from "./hooks/usePOSCart";
import { useCartStore } from "../../stores/cartStore";

export function POSPage() {
  const user = useAuthStore((state) => state.user);
  const selectedStoreId = useAuthStore((state) => state.selectedStoreId);

  const { canBill, sessionId, counterId } = useCanBill();
  const { fetchActiveSession, setShowOpenSessionModal } =
    useBillingSessionStore();

  const currentStoreId = selectedStoreId || user?.storeId;
  const discountEngine = useDiscountEngine();
  const pos = usePOSCart(discountEngine, currentStoreId);

  // Cart state for staleness detection
  const { items, isStale } = useCartStore((state) => ({
    items: state.items,
    isStale: state.isStale,
  }));

  // UI modals
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showPrescriptionUpload, setShowPrescriptionUpload] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [preparedQuote, setPreparedQuote] = useState<SaleQuoteResponse | null>(
    null,
  );
  const [quoting, setQuoting] = useState(false);
  const [lastCreatedInvoice, setLastCreatedInvoice] = useState<{
    id: string;
    invoiceNumber: string;
    quote: SaleQuoteResponse;
    sale: AcceptSaleResponse;
    printAttempted: boolean;
  } | null>(null);

  const getScheduledDrugComplianceIssues = () => {
    if (!pos.hasScheduledDrugs()) {
      return [] as string[];
    }

    const issues: string[] = [];
    const requiresPrescriptionUpload = pos.cart.some((item) =>
      ["SCHEDULE_H", "SCHEDULE_H1"].includes(item.product.scheduleType || ""),
    );

    if (!pos.scheduledDrugDoctorName.trim()) {
      issues.push("Enter the prescribing doctor name.");
    }

    if (!pos.scheduledDrugPrescribedDate) {
      issues.push("Select the prescription date.");
    } else {
      const prescribedDate = new Date(pos.scheduledDrugPrescribedDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (prescribedDate > today) {
        issues.push("Prescription date cannot be in the future.");
      }
    }

    if (requiresPrescriptionUpload && !pos.uploadedPrescription) {
      issues.push("Upload a prescription for Schedule H/H1 medicines.");
    }

    return issues;
  };

  const requestErrorMessage = (error: unknown, fallback: string) => {
    const candidate = error as {
      response?: { data?: { message?: string } };
      message?: string;
    };
    return candidate.response?.data?.message || candidate.message || fallback;
  };

  const quoteCompliance = (quote: SaleQuoteResponse) =>
    collectSaleCompliance(requiredSaleComplianceFields(quote), {
      patientName: pos.selectedCustomer?.name,
      patientAddress: pos.selectedCustomer?.address,
      doctorName: pos.scheduledDrugDoctorName || pos.doctorName,
      doctorRegistration: pos.doctorRegistration,
      prescriptionNumber: pos.uploadedPrescription?.id,
      prescriptionDate: pos.scheduledDrugPrescribedDate,
    });

  const prepareQuote = async () => {
    if (pos.cart.length === 0) {
      return;
    }

    if (!navigator.onLine) {
      const message =
        "Web billing is online-only. Your cart is read-only until the connection returns; no invoice was saved or queued.";
      pos.setError(message);
      showToast.error(message);
      return;
    }

    if (!currentStoreId || quoting) return;

    if (!canBill || !sessionId || !counterId) {
      const recoveredSession = await fetchActiveSession(currentStoreId);
      if (!recoveredSession?.id || !recoveredSession.counterId) {
        showToast.error("Open a counter session before taking payment");
        setShowOpenSessionModal(true);
        return;
      }
      showToast.success("Existing counter session restored");
    }

    if (pos.isReverseCharge) {
      const message =
        "Reverse-charge billing is outside the guided pharmacy sale flow.";
      pos.setError(message);
      showToast.error(message);
      return;
    }

    const issues = getScheduledDrugComplianceIssues();
    if (issues.length > 0) {
      showToast.warning(
        `Scheduled drug warning: ${issues.join(" ")} Sale can continue.`,
      );
    }

    setQuoting(true);
    pos.setError("");
    try {
      const currentTotals = pos.calculateTotals();
      const quoteRequest = buildSaleQuoteRequest({
        customerId: pos.selectedCustomer?.id,
        billDiscountRupees: currentTotals.billDiscountAmount,
        ...(pos.roundOffAmount !== ""
          ? {
              context: {
                commercial: {
                  roundOffPaise: Math.round(
                    (Number(pos.roundOffAmount) || 0) * 100,
                  ),
                },
              },
            }
          : {}),
        lines: pos.computedCart.map((item, index) => {
          const grossRupees = item.rate * item.quantity;
          return {
            lineId: `${pos.activeSessionId}-${index}-${item.product.id}`,
            productId: item.product.id,
            packId: item.salePackId,
            quantity: item.quantity,
            grossRupees,
            discountRupees: item.manualDiscount,
          };
        }),
      });
      const quoteStorageKey = `medicorepay:quote:${pos.activeSessionId}:key`;
      const quoteFingerprint = JSON.stringify(quoteRequest);
      const storedQuote = sessionStorage.getItem(quoteStorageKey);
      let quoteCommand = storedQuote ? JSON.parse(storedQuote) : null;
      if (quoteCommand?.fingerprint !== quoteFingerprint) {
        quoteCommand = {
          fingerprint: quoteFingerprint,
          key: crypto.randomUUID(),
        };
        sessionStorage.setItem(quoteStorageKey, JSON.stringify(quoteCommand));
      }
      const quote = await productRevivalApi.createSaleQuote(quoteRequest, {
        idempotencyKey: quoteCommand.key,
        clientApplication: "web",
      });
      sessionStorage.removeItem(quoteStorageKey);
      const { missing } = quoteCompliance(quote);
      if (issues.length === 0 && missing.length > 0) {
        showToast.warning(
          `Scheduled drug warning: missing ${missing.join(", ")}. Sale can continue.`,
        );
      }
      const serverWarnings = [
        ...new Set(
          quote.lines
            .flatMap((line) => line.warnings ?? [])
            .filter(
              (warning) => warning !== "SERVER_PRESCRIPTION_LINK_REQUIRED",
            ),
        ),
      ];
      if (serverWarnings.includes("STORE_DRUG_LICENCE_INVALID")) {
        showToast.warning(
          "Store drug licence details are missing, expired, or do not cover this schedule. Sale can continue.",
        );
      }
      setPreparedQuote(quote);
      pos.setAmountTendered((quote.payablePaise / 100).toFixed(2));
      setShowPaymentModal(true);
    } catch (error) {
      const message = requestErrorMessage(
        error,
        "Unable to prepare sale quote",
      );
      pos.setError(message);
      showToast.error(message);
    } finally {
      setQuoting(false);
    }
  };

  // Focus product search only when the POS first opens. Keeping this out of the
  // shortcut effect prevents controlled input updates from stealing focus.
  useEffect(() => {
    pos.searchRef.current?.focus();
  }, [pos.searchRef]);

  // Fast refresh or a transient client reset can clear the local session while
  // the server-side counter session remains open. Rehydrate it automatically.
  useEffect(() => {
    if (currentStoreId && !canBill) {
      void fetchActiveSession(currentStoreId);
    }
  }, [canBill, currentStoreId, fetchActiveSession]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyboard = (e: KeyboardEvent) => {
      if (e.key === "F1") {
        e.preventDefault();
        pos.clearCart();
      } else if (e.key === "F2") {
        e.preventDefault();
        pos.searchRef.current?.focus();
      } else if (e.key === "F3") {
        e.preventDefault();
        void prepareQuote();
      } else if (e.key === "Escape") {
        e.preventDefault();
        if (showPaymentModal) setShowPaymentModal(false);
        if (pos.showCustomerDropdown) pos.setShowCustomerDropdown(false);
      }
    };

    window.addEventListener("keydown", handleKeyboard);
    return () => window.removeEventListener("keydown", handleKeyboard);
  }, [prepareQuote, pos.showCustomerDropdown, showPaymentModal]);

  // Computed values
  const totals = pos.calculateTotals();
  const paymentTotal = preparedQuote
    ? preparedQuote.payablePaise / 100
    : totals.grandTotal;
  const displayTotals = preparedQuote
    ? {
        ...totals,
        subtotal: preparedQuote.taxablePaise / 100,
        totalGst: preparedQuote.taxPaise / 100,
        grandTotal: preparedQuote.payablePaise / 100,
      }
    : totals;
  const amountPaid = parseFloat(pos.amountTendered) || 0;
  const changeAmount = amountPaid - paymentTotal;

  useEffect(() => {
    setPreparedQuote(null);
  }, [pos.cart, pos.selectedCustomer?.id]);

  // Applied offers
  const appliedOffers = discountEngine.activeSchemes
    .map((scheme) => {
      const impactTotals = pos.computedCart.reduce(
        (acc, item) => {
          const lineTotal = item.rate * item.quantity;
          const impact = discountEngine.calculateSchemeImpactForScheme(
            scheme,
            item.product,
            lineTotal,
            item.quantity,
          );
          acc.discountAmount += impact.discountAmount;
          acc.freeQuantity += impact.freeQuantity;
          return acc;
        },
        { discountAmount: 0, freeQuantity: 0 },
      );
      return {
        id: scheme.id,
        name: scheme.name || scheme.code || "Offer",
        discountAmount: Number(impactTotals.discountAmount.toFixed(2)),
        freeQuantity: impactTotals.freeQuantity,
      };
    })
    .filter((offer) => offer.discountAmount > 0 || offer.freeQuantity > 0);

  // Payment handler
  const handlePayment = async (
    shouldPrint: boolean = false,
    splitPayments?: SplitPaymentLine[],
  ) => {
    if (!preparedQuote || !sessionId || !counterId) return;
    if (!navigator.onLine) {
      const message =
        "Connection lost before acceptance. Nothing was saved; reconnect and quote the cart again.";
      pos.setError(message);
      showToast.error(message);
      setPreparedQuote(null);
      return;
    }

    let printWindow: Window | null = null;
    if (shouldPrint) {
      try {
        printWindow = openInvoicePrintWindow();
      } catch (error) {
        showToast.error(
          requestErrorMessage(error, "Allow the print window and retry"),
        );
        return;
      }
    }

    pos.setError("");
    pos.setProcessing(true);

    try {
      const offered = splitPayments?.length
        ? splitPayments.map((payment) => ({
            method: payment.method,
            amountRupees: payment.amount,
            reference: payment.reference,
          }))
        : [
            {
              method: pos.paymentMethod,
              amountRupees: parseFloat(pos.amountTendered) || 0,
            },
          ];
      const allocatedTenders = allocateSaleTenders(
        preparedQuote.payablePaise,
        offered,
      );
      const changePaise = allocatedTenders.reduce(
        (sum, tender) =>
          sum +
          (tender.method === "CASH"
            ? (tender.tenderedPaise ?? tender.amountPaise) - tender.amountPaise
            : 0),
        0,
      );
      if (changePaise > 0) {
        const options = await paymentService.getAuthoritativeCashChangeOptions(
          sessionId,
          changePaise,
        );
        if (!options.possible)
          throw new Error(
            `The drawer is short by ₹${(options.shortPaise / 100).toFixed(2)}. Use exact cash, split tender, or bounded round-off.`,
          );
      }
      const { compliance } = quoteCompliance(preparedQuote);
      const tenders = await paymentService.collectCanonicalSaleTenders({
        quoteId: preparedQuote.quoteId,
        billingSessionId: sessionId,
        customerId: preparedQuote.customerId,
        tenders: allocatedTenders,
        clientApplication: "web",
      });
      const storageKey = `medicorepay:sale:${preparedQuote.quoteId}:accept-key`;
      const idempotencyKey =
        sessionStorage.getItem(storageKey) || crypto.randomUUID();
      sessionStorage.setItem(storageKey, idempotencyKey);
      const sale = await productRevivalApi.acceptSale(
        {
          quoteId: preparedQuote.quoteId,
          quoteVersion: preparedQuote.version,
          counterId,
          billingSessionId: sessionId,
          tenders,
          ...(Object.keys(compliance).length ? { compliance } : {}),
          print: false,
        },
        { idempotencyKey, clientApplication: "web" },
      );
      sessionStorage.removeItem(storageKey);

      showToast.success(`Invoice ${sale.invoiceNumber} created successfully!`);

      // Refresh active session so Today's Sales stays current
      if (currentStoreId) {
        fetchActiveSession(currentStoreId);
      }

      setLastCreatedInvoice({
        id: sale.saleId,
        invoiceNumber: sale.invoiceNumber,
        quote: preparedQuote,
        sale,
        printAttempted: false,
      });
      setShowSuccessModal(true);
      setShowPaymentModal(false);
      pos.removeSession(pos.activeSessionId);
      setPreparedQuote(null);
      if (shouldPrint) {
        await printAcceptedSale(sale.saleId, printWindow)
          .then(() =>
            setLastCreatedInvoice((current) =>
              current?.id === sale.saleId
                ? { ...current, printAttempted: true }
                : current,
            ),
          )
          .catch((error) => {
            setLastCreatedInvoice((current) =>
              current?.id === sale.saleId
                ? { ...current, printAttempted: true }
                : current,
            );
            showToast.error(
              requestErrorMessage(
                error,
                "Sale completed, but the invoice could not be printed",
              ),
            );
          });
      }
    } catch (err) {
      if (printWindow && !printWindow.closed) printWindow.close();
      const errorMsg = requestErrorMessage(err, "Failed to accept sale");
      const response = (
        err as {
          response?: {
            status?: number;
            data?: { code?: string; action?: string };
          };
        }
      ).response;
      if (
        response?.status === 409 &&
        (response.data?.code === "QUOTE_STALE" ||
          response.data?.action === "REQUOTE")
      ) {
        const message =
          "The quote changed and was discarded. Press Pay to refresh the latest total.";
        showToast.warning(message);
        pos.setError(message);
        pos.setAmountTendered("");
        setShowPaymentModal(false);
        setPreparedQuote(null);
      } else {
        showToast.error(errorMsg);
        pos.setError(errorMsg);
      }
    } finally {
      pos.setProcessing(false);
    }
  };

  async function printAcceptedSale(
    saleId: string,
    printWindow?: Window | null,
    reason = "ORIGINAL_PRINT",
  ) {
    if (!currentStoreId) throw new Error("Select a store before printing");
    const printers = await printerService.getByStore(currentStoreId);
    const defaultPrinter = printers.find(
      (printer) =>
        printer.isActive && printer.isDefault && printer.purpose === "BILLING",
    );
    const printer =
      (defaultPrinter?.connectionType === "SYSTEM"
        ? defaultPrinter
        : undefined) ??
      printers.find(
        (candidate) =>
          candidate.isActive &&
          candidate.purpose === "BILLING" &&
          candidate.connectionType === "SYSTEM",
      );
    if (!printer) {
      if (printWindow && !printWindow.closed) printWindow.close();
      throw new Error(
        "Configure a system billing printer for web printing, or use MediCorePay Desktop",
      );
    }
    return printerDispatchService.createAndDispatchInvoice({
      saleId,
      printer,
      reason,
      printWindow,
    });
  }

  return (
    <div className="flex h-full flex-col overflow-hidden bg-slate-50 font-sans">
      <POSSessionTabs
        sessions={pos.sessions}
        activeSessionId={pos.activeSessionId}
        setActiveSessionId={pos.setActiveSessionId}
        addSession={pos.addSession}
        removeSession={pos.removeSession}
      />

      <div className="flex min-h-0 flex-1 items-stretch overflow-hidden">
        <div className="flex min-w-0 flex-1 flex-col gap-3 overflow-hidden p-3">
          <ProductSearchBar
            searchRef={pos.searchRef}
            searchTerm={pos.searchTerm}
            setSearchTerm={pos.setSearchTerm}
            searchResults={pos.searchResults}
            compact={pos.cart.length > 0}
            addToCart={pos.addToCart}
          />
          <CartTable
            cart={pos.cart}
            computedCart={pos.computedCart}
            totals={displayTotals}
            updateQuantity={pos.updateQuantity}
            updateDiscount={pos.updateDiscount}
            updateDiscountType={pos.updateDiscountType}
            updatePack={pos.updatePack}
            removeFromCart={pos.removeFromCart}
          />
        </div>

        <POSSidebar
          customerPanel={
            <CustomerPanel
              selectedCustomer={pos.selectedCustomer}
              customerSearch={pos.customerSearch}
              setCustomerSearch={pos.setCustomerSearch}
              customerResults={pos.customerResults}
              showCustomerDropdown={pos.showCustomerDropdown}
              setShowCustomerDropdown={pos.setShowCustomerDropdown}
              searchingCustomer={pos.searchingCustomer}
              isCreatingCustomer={pos.isCreatingCustomer}
              setIsCreatingCustomer={pos.setIsCreatingCustomer}
              newName={pos.newName}
              setNewName={pos.setNewName}
              newPhone={pos.newPhone}
              setNewPhone={pos.setNewPhone}
              selectCustomer={pos.selectCustomer}
              clearCustomer={pos.clearCustomer}
              handleQuickCreate={pos.handleQuickCreate}
              processing={pos.processing}
              setSelectedCustomer={pos.setSelectedCustomer}
            />
          }
          selectedCustomerGstNumber={
            pos.selectedCustomer?.gstNumber ?? undefined
          }
          notes={pos.notes}
          setNotes={pos.setNotes}
          billDiscountType={pos.billDiscountType}
          setBillDiscountType={pos.setBillDiscountType}
          billDiscountValue={pos.billDiscountValue}
          setBillDiscountValue={pos.setBillDiscountValue}
          roundOffAmount={pos.roundOffAmount}
          setRoundOffAmount={pos.setRoundOffAmount}
          appliedOffers={appliedOffers}
          hasScheduledDrugs={pos.hasScheduledDrugs()}
          uploadedPrescription={pos.uploadedPrescription}
          onUploadPrescription={() => setShowPrescriptionUpload(true)}
          scheduledDrugDoctorName={pos.scheduledDrugDoctorName}
          setScheduledDrugDoctorName={pos.setScheduledDrugDoctorName}
          scheduledDrugPrescribedDate={pos.scheduledDrugPrescribedDate}
          setScheduledDrugPrescribedDate={pos.setScheduledDrugPrescribedDate}
          totals={displayTotals}
          cartLength={pos.cart.length}
          clearCart={pos.clearCart}
          onPay={() => void prepareQuote()}
          doctorName={pos.doctorName}
          setDoctorName={pos.setDoctorName}
          doctorRegistration={pos.doctorRegistration}
          setDoctorRegistration={pos.setDoctorRegistration}
          hospitalName={pos.hospitalName}
          setHospitalName={pos.setHospitalName}
          isReverseCharge={pos.isReverseCharge}
          setIsReverseCharge={pos.setIsReverseCharge}
          reverseChargeReason={pos.reverseChargeReason}
          setReverseChargeReason={pos.setReverseChargeReason}
        />
      </div>

      {showPaymentModal && (
        <>
          {isStale && items.length > 0 && (
            <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-950/40 p-4">
              <div className="w-full max-w-md rounded-lg bg-amber-50 shadow-lg border border-amber-200 p-4">
                <div className="flex gap-3">
                  <div className="text-amber-600 text-2xl">⚠️</div>
                  <div>
                    <h3 className="font-semibold text-amber-900">Stale Cart</h3>
                    <p className="text-sm text-amber-800 mt-1">
                      Cart contains items from more than 4 hours ago. Please
                      review before billing.
                    </p>
                    <div className="flex gap-2 mt-4">
                      <button
                        onClick={() => setShowPaymentModal(false)}
                        className="px-4 py-2 bg-amber-600 text-white rounded font-medium hover:bg-amber-700"
                      >
                        Continue
                      </button>
                      <button
                        onClick={() => {
                          useCartStore.setState({
                            items: [],
                            customerId: undefined,
                            customerName: undefined,
                          });
                          setShowPaymentModal(false);
                        }}
                        className="px-4 py-2 bg-white text-amber-700 rounded font-medium border border-amber-200 hover:bg-amber-50"
                      >
                        Clear Cart
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <PaymentModal
            totals={{ ...displayTotals, grandTotal: paymentTotal }}
            paymentMethod={pos.paymentMethod}
            setPaymentMethod={pos.setPaymentMethod}
            amountTendered={pos.amountTendered}
            setAmountTendered={pos.setAmountTendered}
            changeAmount={changeAmount}
            processing={pos.processing}
            error={pos.error}
            allowedMethods={preparedQuote?.allowedTenders}
            onPayment={handlePayment}
            onClose={() => setShowPaymentModal(false)}
          />
        </>
      )}

      {showSuccessModal && lastCreatedInvoice && (
        <SaleSuccessModal
          invoice={lastCreatedInvoice}
          onClose={() => setShowSuccessModal(false)}
          onPrint={() => {
            const reason = lastCreatedInvoice.printAttempted
              ? INVOICE_REPRINT_AUDIT_REASON
              : "ORIGINAL_PRINT";
            try {
              const printWindow = openInvoicePrintWindow();
              void printAcceptedSale(lastCreatedInvoice.id, printWindow, reason)
                .then(() =>
                  setLastCreatedInvoice((current) =>
                    current ? { ...current, printAttempted: true } : current,
                  ),
                )
                .catch((error) => {
                  setLastCreatedInvoice((current) =>
                    current ? { ...current, printAttempted: true } : current,
                  );
                  showToast.error(
                    requestErrorMessage(error, "Unable to print invoice"),
                  );
                });
            } catch (error) {
              showToast.error(
                requestErrorMessage(error, "Allow the print window and retry"),
              );
            }
          }}
        />
      )}

      {showPrescriptionUpload && (
        <PrescriptionUpload
          customerId={pos.selectedCustomer?.id}
          patientName={pos.selectedCustomer?.name || "Walk-in"}
          doctorName={pos.scheduledDrugDoctorName}
          onUploadSuccess={(prescription) => {
            pos.setUploadedPrescription(prescription);
            setShowPrescriptionUpload(false);
            showToast.success("Prescription uploaded successfully");
          }}
          onClose={() => setShowPrescriptionUpload(false)}
        />
      )}
    </div>
  );
}
